import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchTransferPlotsThunk } from '../store/slices/transferPlotSlice'
import { FileText, Loader2, Printer } from 'lucide-react'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'

import printJS from 'print-js'

const TransferPlotList = () => {
  const dispatch = useDispatch()
  const { transferPlots, status, error } = useSelector(
    state => state.transferPlot
  )
  const { activeUser } = useSelector(state => state.auth)
  const { activeSociety, theme, gradient } = useSidebarContext()

  useEffect(() => {
    // Check if user is authorized
    if (
      activeUser?.role === 'Admin' ||
      (activeUser?.role === 'Employee' &&
        activeUser?.additionalData?.designation === 'Manager')
    ) {
      dispatch(fetchTransferPlotsThunk())
    }
  }, [dispatch, activeUser])

  const filteredPlots = activeSociety
    ? transferPlots.filter(
      plot =>
        plot.plot_id &&
        plot.plot_id.society_id &&
        plot.plot_id.society_id._id === activeSociety._id
    )
    : []

  const handlePrintSlip = plot => {
    const societyImage = plot.plot_id?.society_id?.society_image || ''
    const societyName = plot.plot_id?.society_id?.name || 'Real Estate Society'
    const societyLocation =
      plot.plot_id?.society_id?.location || 'Real Estate Society'
    const plotNumber = plot.plot_id?.plot_number || 'N/A'
    const block = plot.plot_id?.block || 'N/A'
    const size = plot.plot_id?.size || 'N/A'
    const plotType = plot.plot_id?.plot_type || 'Residential'
    const transferDate = plot.transfer_date
      ? new Date(plot.transfer_date).toLocaleDateString('en-GB')
      : 'N/A'
    const transferFee = plot.transfer_fee
      ? plot.transfer_fee.toLocaleString()
      : 'N/A'
    const bookingStatus = plot.plot_id?.booking_status || 'N/A'
    const ownerName = plot.new_owner_id?.customerData?.name || 'N/A'
    const ownerUsername = plot.new_owner_id?.username || 'N/A'
    const ownerPhone =
      plot.new_owner_id?.customerData?.contactInfo?.phone || 'N/A'
    const ownerEmail =
      plot.new_owner_id?.customerData?.contactInfo?.email || 'N/A'
    const ownerAddress =
      plot.new_owner_id?.customerData?.contactInfo?.address || 'N/A'
    const refNumber = `PCT-${new Date().getFullYear()}-${String(
      Math.floor(Math.random() * 10000)
    ).padStart(4, '0')}`

    const slipContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Plot Transfer Certificate</title>
        <link href="https://cdnjs.cloudflare.com/ajax/libs/tailwindcss/2.2.19/tailwind.min.css" rel="stylesheet">
        <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Playfair+Display:wght@700;800&display=swap');
    
    :root {
        --primary-blue: #0066ff;
        --dark-blue: #0044cc;
        --light-blue: #e6f0ff;
        --accent-blue: #3366ff;
        --gray-dark: #1a1a1a;
        --gray-light: #f5f7fa;
        --gray-medium: #8b92a4;
    }

    * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    @page {
        size: A4;
        margin: 0;
    }

    html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
    }

    body {
        font-family: 'Poppins', sans-serif;
        background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
        width: 210mm;
        height: 297mm;
        margin: 0 auto;
        padding: 0;
        font-size: 11px;
    }

    .certificate-wrapper {
        width: 210mm;
        height: 297mm;
        padding: 6mm;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    .certificate-container {
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%);
        backdrop-filter: blur(20px);
        border-radius: 24px;
        box-shadow: 0 25px 50px rgba(0, 102, 255, 0.12), 
                    0 0 1px rgba(0, 0, 0, 0.08),
                    inset 0 1px 0 rgba(255, 255, 255, 0.9);
        border: 1.5px solid rgba(255, 255, 255, 0.9);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
    }

    .certificate-container::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 15% 40%, rgba(0, 102, 255, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 85% 85%, rgba(0, 102, 255, 0.05) 0%, transparent 50%);
        pointer-events: none;
    }

    .header {
        background: linear-gradient(135deg, #0066ff 0%, #0052cc 100%);
        color: white;
        padding: 1.2rem 1.2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        overflow: hidden;
        flex-shrink: 0;
        border-bottom: 2px solid rgba(255,255,255,0.1);
    }

    .header-left {
        display: flex;
        align-items: center;
        gap: 1rem;
        position: relative;
        z-index: 2;
    }

    .header-logo {
        width: 55px;
        height: 55px;
        object-fit: cover;
        border-radius: 14px;
        border: 2.5px solid rgba(255,255,255,0.4);
        box-shadow: 0 8px 16px rgba(0,0,0,0.15);
    }

    .header-text h1 {
        font-family: 'Playfair Display', serif;
        font-size: 1.45rem;
        font-weight: 800;
        margin: 0;
        letter-spacing: -0.6px;
        line-height: 1.1;
        color: white;
    }

    .header-text p {
        font-size: 0.75rem;
        opacity: 0.95;
        margin-top: 0.2rem;
        font-weight: 500;
        letter-spacing: 0.6px;
        text-transform: uppercase;
    }

    .header-right {
        text-align: right;
        position: relative;
        z-index: 2;
    }

    .header-right p:first-child {
        font-size: 0.8rem;
        font-weight: 600;
        opacity: 0.95;
        letter-spacing: 0.4px;
        text-transform: uppercase;
    }

    .ref-badge {
        background: rgba(255,255,255,0.2);
        backdrop-filter: blur(15px);
        border: 1.5px solid rgba(255,255,255,0.5);
        padding: 0.45rem 0.85rem;
        border-radius: 10px;
        margin-top: 0.4rem;
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.6px;
        color: white;
        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
    }

    .content {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.9rem;
        padding: 1.1rem;
        flex-grow: 1;
        overflow: hidden;
        position: relative;
        z-index: 1;
    }

    .section {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 250, 252, 0.6) 100%);
        backdrop-filter: blur(25px);
        border-radius: 16px;
        padding: 1rem;
        border: 1.5px solid rgba(0, 102, 255, 0.15);
        box-shadow: 0 10px 40px rgba(0, 102, 255, 0.1),
                    inset 0 1px 0 rgba(255, 255, 255, 0.8);
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        min-height: auto;
    }

    .section::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -50%;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(0, 102, 255, 0.05) 0%, transparent 70%);
        pointer-events: none;
    }

    .section-title {
        font-weight: 800;
        font-size: 1.15rem;
        color: #0066ff;
        border-bottom: 2.5px solid #0066ff;
        padding-bottom: 0.6rem;
        margin-bottom: 0.7rem;
        letter-spacing: 0.2px;
        position: relative;
        z-index: 2;
        background: linear-gradient(135deg, #0066ff, #0052cc);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        text-transform: uppercase;
        font-size: 1.1rem;
    }

    .info-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.55rem;
        padding: 0.4rem 0;
        border-bottom: 1px solid rgba(0, 102, 255, 0.12);
        position: relative;
        z-index: 2;
        gap: 0.8rem;
    }

    .info-row:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }

    .info-label {
        color: #8b92a4;
        font-weight: 600;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        flex-shrink: 0;
    }

    .info-value {
        font-weight: 700;
        color: #1a1a1a;
        text-align: right;
        font-size: 0.85rem;
        word-break: break-word;
        position: relative;
        z-index: 2;
    }

    .highlight-value {
        background: linear-gradient(135deg, #0066ff, #0052cc);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        font-weight: 800;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background: linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(0, 102, 255, 0.08));
        border-radius: 10px;
        font-size: 0.9rem;
        font-weight: 700;
        color: #0066ff;
        border: 1.5px solid rgba(0, 102, 255, 0.25);
        box-shadow: 0 4px 12px rgba(0, 102, 255, 0.08);
    }

    .signature-area {
        grid-column: span 2;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.8rem;
        margin-top: 0.8rem;
        padding-top: 0.8rem;
        border-top: 2px dashed rgba(0, 102, 255, 0.25);
        position: relative;
        z-index: 2;
    }

    .signature-box {
        text-align: center;
        padding: 0.9rem;
        border: 2px dashed rgba(0, 102, 255, 0.35);
        border-radius: 12px;
        background: linear-gradient(135deg, rgba(0, 102, 255, 0.05), rgba(0, 102, 255, 0.02));
        min-height: 55px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .signature-box p:first-child {
        font-weight: 700;
        font-size: 0.85rem;
        color: #0066ff;
        margin-bottom: 0.25rem;
    }

    .signature-box p:last-child {
        font-size: 0.7rem;
        color: #8b92a4;
        font-weight: 500;
    }

    .terms-section {
        grid-column: span 2;
        background: linear-gradient(135deg, rgba(230, 240, 255, 0.6), rgba(240, 245, 255, 0.5));
        backdrop-filter: blur(25px);
        border-radius: 16px;
        padding: 1rem;
        margin-top: 0.8rem;
        border: 1.5px solid rgba(0, 102, 255, 0.18);
        position: relative;
        z-index: 2;
        box-shadow: 0 10px 40px rgba(0, 102, 255, 0.08);
    }

    .terms-section h3 {
        color: #0066ff;
        margin-bottom: 0.5rem;
        font-weight: 800;
        font-size: 1.1rem;
        letter-spacing: 0.2px;
        background: linear-gradient(135deg, #0066ff, #0052cc);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        text-transform: uppercase;
    }

    .terms-section ol {
        color: #1a1a1a;
        padding-left: 1.3rem;
        font-size: 0.7rem;
        line-height: 1.5;
        font-weight: 500;
    }

    .terms-section li {
        margin-bottom: 0.3rem;
        color: #5a5f6b;
    }

    .footer {
        height: 4px;
        background: linear-gradient(90deg, #0066ff 0%, #0052cc 50%, #0066ff 100%);
        position: relative;
        z-index: 1;
        flex-shrink: 0;
        box-shadow: 0 4px 15px rgba(0, 102, 255, 0.2);
    }

    @media print {
        html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
        }
        body {
            background: white;
        }
        .certificate-wrapper {
            padding: 0;
        }
        .certificate-container {
            border-radius: 0;
            box-shadow: none;
            border: none;
        }
        body {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        @page {
            size: A4;
            margin: 0;
        }
    }
</style>
    </head>
    <body>
        <div class="certificate-wrapper">
            <div class="certificate-container">
                <!-- Header -->
                <div class="header">
                    <div class="header-left">
                    <img src="${societyImage}" alt="Society" class="header-logo">
                        <div class="header-text">
                            <h1>${societyName}</h1>
                            <p>${societyLocation}</p>
                        </div>
                    </div>
                    <div class="header-right">
                        <p>TRANSFER REFERENCE</p>
                        <div class="ref-badge">${refNumber}</div>
                    </div>
                </div>

                <!-- Content -->
                <div class="content">
                    <!-- Plot Details -->
                    <div class="section">
                        <h2 class="section-title">📍 Plot Details</h2>
                        <div class="info-row">
                            <span class="info-label">Plot Number</span>
                            <span class="info-value highlight-value">${plotNumber}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Location</span>
                            <span class="info-value">${block} Block</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Area</span>
                            <span class="info-value highlight-value">${size} Marla</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Land Use</span>
                            <span class="info-value">${plotType}</span>
                        </div>
                    </div>

                    <!-- Transfer Information -->
                    <div class="section">
                        <h2 class="section-title">📋 Transfer Information</h2>
                        <div class="info-row">
                            <span class="info-label">Transfer Date</span>
                            <span class="info-value">${transferDate}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Transfer Fee</span>
                            <span class="info-value highlight-value">PKR ${transferFee}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Status</span>
                            <span class="status-badge">● ${bookingStatus}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Processing Time</span>
                            <span class="info-value">7-10 Business Days</span>
                        </div>
                    </div>

                    <!-- Owner Information -->
                    <div class="section">
                        <h2 class="section-title">👤 Owner Information</h2>
                        <div class="info-row">
                            <span class="info-label">Full Name</span>
                            <span class="info-value">${ownerName}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Username</span>
                            <span class="info-value highlight-value">${ownerUsername}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Contact</span>
                            <span class="info-value">${ownerPhone}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Email</span>
                            <span class="info-value">${ownerEmail}</span>
                        </div>
                    </div>

                    <!-- Previous Owner -->
                    <div class="section">
                        <h2 class="section-title">✓ Verification</h2>
                        <div class="info-row">
                            <span class="info-label">Document Type</span>
                            <span class="info-value">Transfer Certificate</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Issued Date</span>
                            <span class="info-value">${new Date().toLocaleDateString(
      'en-GB'
    )}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Validity</span>
                            <span class="info-value">Permanent</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Address</span>
                            <span class="info-value">${ownerAddress}</span>
                        </div>
                    </div>

                    <!-- Signature Area -->
                    <div class="signature-area">
                        <div class="signature-box">
                            <p>Nominee Signature</p>
                            <p>Sign & Date</p>
                        </div>
                        <div class="signature-box">
                            <p>New Owner</p>
                            <p>Sign & Date</p>
                        </div>
                        <div class="signature-box">
                            <p>Authorized Signatory</p>
                            <p>Official Stamp</p>
                        </div>
                    </div>

                    <!-- Terms and Conditions -->
                    <div class="terms-section">
                        <h3>⚖️ Terms & Conditions</h3>
                        <ol>
                            <li>This certificate confirms the legal transfer of plot ownership within the society.</li>
                            <li>The new owner must comply with all society bylaws and regulations.</li>
                            <li>Transfer fees are non-refundable and subject to change without prior notice.</li>
                            <li>All property taxes and outstanding dues must be cleared before transfer.</li>
                            <li>The society reserves the right to verify and validate all transfer documents.</li>
                            <li>This certificate is valid only with original signatures and society seal.</li>
                            <li>Any disputes shall be subject to the jurisdiction of local real estate regulations.</li>
                        </ol>
                    </div>
                </div>

                <!-- Footer -->
                <div class="footer"></div>
            </div>
        </div>
    </body>
    </html>
  `

    // Use window.open for more reliable printing
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(slipContent)
      printWindow.document.close()
      printWindow.focus()

      // Wait for images to load before printing
      setTimeout(() => {
        printWindow.print()
      }, 500)
    }
  }

  if (status === 'loading') {
    return (
      <div
        className={`min-h-screen bg-gray-900 flex items-center justify-center`}
      >
        <div className='flex flex-col items-center space-y-4'>
          <Loader2 className={`w-12 h-12 text-teal-400 animate-spin`} />
          <p className={`text-teal-400 text-lg font-medium`}>
            Loading transfers...
          </p>
        </div>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div
        className={`min-h-screen bg-gray-900 flex items-center justify-center`}
      >
        <div className={`bg-gray-800 p-6 rounded-lg shadow-xl`}>
          <h2 className='text-red-400 text-xl font-bold mb-2'>Error</h2>
          <p className='text-gray-300'>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-8`}
    >
      <div className='max-w-7xl mx-auto'>
        <div className='flex items-center mb-10 space-x-4'>
          <FileText className={`w-10 h-10 ${theme.tailwind.text9}`} />
          <h1
            className={`text-4xl font-extrabold ${theme.tailwind.text9} tracking-tight`}
          >
            Transferred Plots for {activeSociety?.name || 'All Societies'}
          </h1>
        </div>

        <div
          className={`${theme.tailwind.bg6} backdrop-blur-sm rounded-2xl shadow-2xl border ${theme.tailwind.border2} overflow-hidden`}
        >
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead
                className={`${theme.tailwind.bg32} border-b ${theme.tailwind.border2}`}
              >
                <tr>
                  {[
                    'Plot No.',
                    'Owner username',
                    'Society',
                    'Contact No.',
                    'Transfer Fee',
                    'Transfer Date',
                    'Actions'
                  ].map(header => (
                    <th
                      key={header}
                      className={`px-6 py-4 text-left text-sm font-semibold ${theme.tailwind.text9} uppercase tracking-wider`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              {filteredPlots.length > 0 ? (
                <tbody>
                  {filteredPlots.map((plot, index) => (
                    <tr
                      key={plot.plot_id.plot_number}
                      className={`transition-all duration-300 ease-in-out ${theme.tailwind.bgHover21
                        } ${index % 2 === 0
                          ? `${theme.tailwind.bg34}`
                          : `${theme.tailwind.bg2}`
                        }`}
                    >
                      {[
                        plot.plot_id.plot_number,
                        plot.new_owner_id?.username || 'N/A',
                        plot.plot_id.society_id?.name || 'N/A',
                        plot.new_owner_id?.customerData?.contactInfo.phone ||
                        'N/A',
                        plot.transfer_fee
                          ? `${plot.transfer_fee.toLocaleString()} PKR`
                          : 'N/A',
                        plot.transfer_date
                          ? new Date(plot.transfer_date).toLocaleDateString(
                            'en-GB'
                          )
                          : 'N/A'
                      ].map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={`px-6 py-4 ${theme.tailwind.text9} whitespace-nowrap text-sm`}
                        >
                          {cell}
                        </td>
                      ))}
                      <td className='px-6 py-4'>
                        <button
                          onClick={() => handlePrintSlip(plot)}
                          className={`flex items-center space-x-2 ${theme.tailwind.bg41} ${theme.tailwind.text9} px-4 py-2 rounded-lg ${theme.tailwind.yellow} transition-all duration-300 border ${theme.tailwind.border11} shadow-md hover:shadow-lg`}
                        >
                          <Printer className='w-4 h-4' />
                          <span>Print Slip</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td
                      colSpan='7'
                      className={`px-6 py-12 text-center ${theme.tailwind.text9} text-lg font-medium`}
                    >
                      <div className='flex flex-col items-center justify-center space-y-4'>
                        <FileText className='w-12 h-12 opacity-50' />
                        <p>
                          No transferred plots available for{' '}
                          {activeSociety?.name || 'any society'}
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransferPlotList
